import { useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clearToken } from '../../oauth/oauthUtils';

// Keycloak loads this in a hidden iframe when a DIFFERENT app sharing this
// realm ends its session (RP-initiated logout), via the client's configured
// Front-channel logout URL. Clearing tokens here fires a 'storage' event
// that any other open tab of this app picks up (see ProtectedRoute), so
// this app logs out too instead of sitting on a stale session.
//
// useLayoutEffect (not useEffect) is required here: Keycloak's own
// frontchannel-logout page redirects away as soon as this iframe's
// document.readyState hits 'complete', which happens right after the
// initial synchronous render - a plain useEffect is scheduled for after
// that point and can lose the race, leaving clearToken() never called.
const FrontChannelLogout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    const iss = searchParams.get('iss');
    if (iss && iss !== process.env.REACT_APP_OAUTH_ISSUER) {
      // Log but don't block on this - worst case of clearing anyway is an
      // early logout, not a security issue, and a strict mismatch here has
      // silently disabled this whole feature before (env misconfiguration).
      console.warn('Front-channel logout: iss mismatch', iss, process.env.REACT_APP_OAUTH_ISSUER);
    }
    clearToken();
    // Defensive: this route is meant to load invisibly in a hidden iframe,
    // but if it's ever hit directly (or the iframe isn't hidden), don't
    // leave the user staring at a blank page.
    navigate('/login', { replace: true });
  }, [searchParams, navigate]);

  return null;
};

export default FrontChannelLogout;
